import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  X,
  Camera,
  RotateCw,
  RefreshCw,
  Check,
  Crop,
  Smartphone,
  Monitor,
} from 'lucide-react';
import { apiClient } from '@/api/axios';
import { QRCodeCanvas } from '../shared/QRCodeCanvas';

const documentTypes = [
  { value: 'vaccination-record', label: 'Vaccination Record' },
  { value: 'health-certificate', label: 'Health Certificate' },
  { value: 'dna-report', label: 'DNA Report' },
  { value: 'ownership-record', label: 'Ownership Record' },
  { value: 'microchip-certificate', label: 'Microchip Certificate' },
  { value: 'pedigree-certificate', label: 'Pedigree Certificate' },
  { value: 'insurance-document', label: 'Insurance Document' },
  { value: 'medical-record', label: 'Medical Record' },
  { value: 'lab-result', label: 'Lab Result' },
  { value: 'x-ray-scan', label: 'X-Ray/Scan' },
  { value: 'surgical-report', label: 'Surgical Report' },
  { value: 'dental-record', label: 'Dental Record' },
  { value: 'passport', label: 'Passport' },
  { value: 'other', label: 'Other' },
];

type Step =
  | 'choose-device'
  | 'phone-instructions'
  | 'camera'
  | 'preview'
  | 'metadata';

interface ScanDocumentModalProps {
  onClose: () => void;
  onUpload: (document: {
    name: string;
    type: string;
    date: string;
    file: File;
    notes?: string;
  }) => void;
}

/** Draw image with rotation and optional crop to canvas, return blob. */
async function imageToBlob(
  imageUrl: string,
  rotation: number,
  crop: { x: number; y: number; w: number; h: number } | null,
  mimeType: 'image/png' | 'image/jpeg' = 'image/png'
): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const c = document.createElement('canvas');
      const ctx = c.getContext('2d');
      if (!ctx) {
        reject(new Error('Canvas not supported'));
        return;
      }
      const w = img.naturalWidth;
      const h = img.naturalHeight;
      const rad = (rotation * Math.PI) / 180;
      const cos = Math.abs(Math.cos(rad));
      const sin = Math.abs(Math.sin(rad));
      const cw = Math.round(w * cos + h * sin);
      const ch = Math.round(w * sin + h * cos);
      c.width = cw;
      c.height = ch;
      ctx.translate(cw / 2, ch / 2);
      ctx.rotate(rad);
      ctx.translate(-w / 2, -h / 2);
      ctx.drawImage(img, 0, 0);
      ctx.setTransform(1, 0, 0, 1, 0, 0);

      let srcW = cw;
      let srcH = ch;
      let sx = 0;
      let sy = 0;
      if (crop && (crop.w < 1 || crop.h < 1)) {
        sx = Math.round(cw * crop.x);
        sy = Math.round(ch * crop.y);
        srcW = Math.round(cw * crop.w);
        srcH = Math.round(ch * crop.h);
      }
      if (crop && (crop.w < 1 || crop.h < 1) && srcW > 0 && srcH > 0) {
        const c2 = document.createElement('canvas');
        c2.width = srcW;
        c2.height = srcH;
        const ctx2 = c2.getContext('2d');
        if (ctx2) {
          ctx2.drawImage(c, sx, sy, srcW, srcH, 0, 0, srcW, srcH);
          c2.toBlob(
            (blob) =>
              blob ? resolve(blob) : reject(new Error('toBlob failed')),
            mimeType,
            0.92
          );
          return;
        }
      }
      c.toBlob(
        (blob) => (blob ? resolve(blob) : reject(new Error('toBlob failed'))),
        mimeType,
        0.92
      );
    };
    img.onerror = () => reject(new Error('Image load failed'));
    img.src = imageUrl;
  });
}

/** Documents page URL for "open on phone" – same origin, dashboard documents route. */
function getDocumentsUrl(): string {
  if (typeof window === 'undefined') return '';
  const origin = window.location.origin;
  const path = window.location.pathname;
  const dashboardIdx = path.indexOf('/dashboard');
  const base = dashboardIdx >= 0 ? path.slice(0, dashboardIdx) : '';
  return `${origin}${base || ''}/dashboard/documents`;
}

export function ScanDocumentModal({
  onClose,
  onUpload,
}: ScanDocumentModalProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [step, setStep] = useState<Step>('choose-device');
  const documentsUrl = useMemo(() => getDocumentsUrl(), []);
  const [cameraError, setCameraError] = useState<string | null>(null);
  const [capturedUrl, setCapturedUrl] = useState<string | null>(null);
  const [rotation, setRotation] = useState(0);
  const [crop, setCrop] = useState<{
    x: number;
    y: number;
    w: number;
    h: number;
  } | null>(null);
  const [processedFile, setProcessedFile] = useState<File | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    type: '',
    date: new Date().toISOString().split('T')[0],
    notes: '',
  });
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [linkCopied, setLinkCopied] = useState(false);

  const startCamera = useCallback(async () => {
    setCameraError(null);
    const isLikelyMobile =
      typeof window !== 'undefined' &&
      /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
        navigator.userAgent
      );
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: isLikelyMobile ? 'environment' : 'user',
          width: { ideal: 1280 },
          height: { ideal: 720 },
        },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Camera access failed';
      if (msg.includes('Permission') || msg.includes('denied')) {
        setCameraError(
          'Camera permission denied. Please allow camera access and try again.'
        );
      } else if (msg.includes('NotFound')) {
        setCameraError('No camera found. Please use a device with a camera.');
      } else {
        setCameraError(msg);
      }
    }
  }, []);

  useEffect(() => {
    if (step === 'camera') {
      startCamera();
    }
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      }
      if (videoRef.current) {
        videoRef.current.srcObject = null;
      }
    };
  }, [step, startCamera]);

  const handleCapture = () => {
    if (!videoRef.current || !streamRef.current) return;
    const video = videoRef.current;
    const c = document.createElement('canvas');
    c.width = video.videoWidth;
    c.height = video.videoHeight;
    const ctx = c.getContext('2d');
    if (!ctx) return;
    ctx.drawImage(video, 0, 0);
    const url = c.toDataURL('image/png');
    streamRef.current.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    if (videoRef.current) videoRef.current.srcObject = null;
    setCapturedUrl(url);
    setRotation(0);
    setCrop(null);
    setStep('preview');
  };

  const handleRetake = () => {
    if (capturedUrl && capturedUrl.startsWith('blob:')) {
      URL.revokeObjectURL(capturedUrl);
    }
    setCapturedUrl(null);
    setStep('camera');
    startCamera();
  };

  const handleRotate = () => {
    setRotation((r) => (r + 90) % 360);
  };

  const handleCropToCenter = () => {
    setCrop((prev) =>
      prev && prev.w === 0.85 ? null : { x: 0.075, y: 0.075, w: 0.85, h: 0.85 }
    );
  };

  const handleUseThis = async () => {
    if (!capturedUrl) return;
    setError(null);
    try {
      const mimeType: 'image/png' | 'image/jpeg' = 'image/png';
      const blob = await imageToBlob(capturedUrl, rotation, crop, mimeType);
      const ext = mimeType === 'image/jpeg' ? 'jpg' : 'png';
      const file = new File([blob], `scan.${ext}`, { type: mimeType });
      setProcessedFile(file);
      if (!formData.name)
        setFormData((f) => ({ ...f, name: 'Scanned document' }));
      setStep('metadata');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to process image');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!processedFile) return;
    setError(null);
    setUploading(true);
    try {
      const contentType = processedFile.type || 'image/png';
      const initRes = await apiClient.post<{
        uploadUrl: string;
        key: string;
      }>('/documents/upload-init', {
        filename: processedFile.name,
        contentType,
        folderPrefix: formData.type ? `${formData.type}/` : undefined,
        ttlSeconds: 900,
        displayName: formData.name.trim() || undefined,
      });
      const { uploadUrl } = initRes.data;
      await apiClient.put(uploadUrl, processedFile, {
        headers: { 'Content-Type': contentType },
      });
      onUpload({
        ...formData,
        file: processedFile,
        notes: formData.notes || undefined,
      });
      onClose();
    } catch (err: unknown) {
      console.error('Upload failed', err);
      setError(
        err instanceof Error ? err.message : 'Upload failed. Please try again.'
      );
    } finally {
      setUploading(false);
    }
  };

  const handleClose = () => {
    if (capturedUrl && capturedUrl.startsWith('blob:'))
      URL.revokeObjectURL(capturedUrl);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-[#16151D] rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col border dark:border-white/8">
        <div className="bg-gradient-to-r from-purple-600 to-blue-600 text-white p-4 flex justify-between items-center shrink-0">
          <h2 className="text-lg font-semibold">
            {step === 'choose-device' && 'How do you want to scan?'}
            {step === 'phone-instructions' && 'Scan with your phone'}
            {step === 'camera' && 'Scan / take photo'}
            {step === 'preview' && 'Adjust & use'}
            {step === 'metadata' && 'Document details'}
          </h2>
          <button
            type="button"
            onClick={handleClose}
            className="p-1 text-white/80 hover:text-white"
            aria-label="Close"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {error && (
          <div className="mx-4 mt-2 text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/40 rounded-lg p-3">
            {error}
          </div>
        )}

        <div className="flex-1 overflow-auto p-4">
          {step === 'choose-device' && (
            <div className="space-y-4">
              <p className="text-sm text-gray-600 dark:text-white/70 text-center">
                Choose where to use the camera: this device (laptop webcam or
                phone) or open the app on your phone.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <button
                  type="button"
                  onClick={() => setStep('camera')}
                  className="flex flex-col items-center gap-3 p-6 rounded-xl border-2 border-gray-200 dark:border-white/10 hover:border-blue-500 dark:hover:border-blue-500/50 hover:bg-blue-50/50 dark:hover:bg-blue-900/10 transition-colors text-left"
                >
                  <div className="w-14 h-14 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center shrink-0">
                    <Monitor className="w-7 h-7 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div className="text-center">
                    <div className="font-medium dark:text-white">
                      Use this device&apos;s camera
                    </div>
                    <div className="text-sm text-gray-500 dark:text-white/60 mt-0.5">
                      Laptop webcam or this phone&apos;s camera
                    </div>
                  </div>
                </button>
                <button
                  type="button"
                  onClick={() => setStep('phone-instructions')}
                  className="flex flex-col items-center gap-3 p-6 rounded-xl border-2 border-gray-200 dark:border-white/10 hover:border-purple-500 dark:hover:border-purple-500/50 hover:bg-purple-50/50 dark:hover:bg-purple-900/10 transition-colors text-left"
                >
                  <div className="w-14 h-14 rounded-full bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center shrink-0">
                    <Smartphone className="w-7 h-7 text-purple-600 dark:text-purple-400" />
                  </div>
                  <div className="text-center">
                    <div className="font-medium dark:text-white">
                      Use my phone
                    </div>
                    <div className="text-sm text-gray-500 dark:text-white/60 mt-0.5">
                      Open Petso on your phone and scan there
                    </div>
                  </div>
                </button>
              </div>
            </div>
          )}

          {step === 'phone-instructions' && (
            <div className="space-y-4">
              <p className="text-sm text-gray-600 dark:text-white/70">
                Open the link below on your phone. Then go to{' '}
                <strong>Documents</strong> → tap <strong>Upload</strong> →{' '}
                <strong>Scan / take photo</strong> to use your phone&apos;s
                camera.
              </p>
              <div className="flex flex-col items-center gap-4">
                {documentsUrl ? (
                  <>
                    <div className="p-4 bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-white/10">
                      <QRCodeCanvas
                        value={documentsUrl}
                        size={200}
                        className="rounded"
                      />
                    </div>
                    <p className="text-xs text-gray-500 dark:text-white/50">
                      Scan this QR code with your phone
                    </p>
                    <div className="w-full flex gap-2">
                      <input
                        type="text"
                        readOnly
                        value={documentsUrl}
                        className="flex-1 px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-white/10 bg-gray-50 dark:bg-[#0F0E16] text-gray-700 dark:text-white"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          navigator.clipboard
                            .writeText(documentsUrl)
                            .then(() => {
                              setLinkCopied(true);
                              setTimeout(() => setLinkCopied(false), 2000);
                            });
                        }}
                        className="px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm shrink-0"
                      >
                        {linkCopied ? 'Copied!' : 'Copy link'}
                      </button>
                    </div>
                  </>
                ) : (
                  <p className="text-sm text-gray-500 dark:text-white/50">
                    Unable to generate link.
                  </p>
                )}
              </div>
              <div className="flex justify-center pt-2">
                <button
                  type="button"
                  onClick={() => setStep('choose-device')}
                  className="px-4 py-2 rounded-lg border dark:border-white/10 dark:text-white hover:bg-gray-100 dark:hover:bg-white/5"
                >
                  Back
                </button>
              </div>
            </div>
          )}

          {step === 'camera' && (
            <div className="space-y-4">
              {cameraError ? (
                <div className="text-center py-8 text-gray-600 dark:text-white/70">
                  <Camera className="w-12 h-12 mx-auto mb-3 text-gray-400 dark:text-white/40" />
                  <p className="mb-4">{cameraError}</p>
                  <button
                    type="button"
                    onClick={startCamera}
                    className="px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700"
                  >
                    Try again
                  </button>
                </div>
              ) : (
                <>
                  <div className="relative aspect-[4/3] max-h-[50vh] mx-auto bg-black rounded-lg overflow-hidden">
                    <video
                      ref={videoRef}
                      autoPlay
                      playsInline
                      muted
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="flex justify-center">
                    <button
                      type="button"
                      onClick={handleCapture}
                      className="flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-full"
                    >
                      <Camera className="w-5 h-5" />
                      Capture
                    </button>
                  </div>
                </>
              )}
            </div>
          )}

          {step === 'preview' && capturedUrl && (
            <div className="space-y-4">
              <div
                className="relative mx-auto bg-gray-100 dark:bg-gray-900 rounded-lg overflow-hidden flex items-center justify-center"
                style={{ minHeight: 280 }}
              >
                <img
                  src={capturedUrl}
                  alt="Captured"
                  className="max-w-full max-h-[50vh] object-contain"
                  style={{
                    transform: `rotate(${rotation}deg)`,
                  }}
                />
              </div>
              <div className="flex flex-wrap justify-center gap-2">
                <button
                  type="button"
                  onClick={handleRotate}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg border dark:border-white/10 hover:bg-gray-100 dark:hover:bg-white/5 dark:text-white"
                >
                  <RotateCw className="w-4 h-4" />
                  Rotate
                </button>
                <button
                  type="button"
                  onClick={handleCropToCenter}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg border dark:border-white/10 hover:bg-gray-100 dark:hover:bg-white/5 dark:text-white ${crop ? 'ring-2 ring-blue-500' : ''}`}
                  title={
                    crop
                      ? 'Crop to center (click again to clear)'
                      : 'Crop to center'
                  }
                >
                  <Crop className="w-4 h-4" />
                  Crop
                </button>
                <button
                  type="button"
                  onClick={handleRetake}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg border dark:border-white/10 hover:bg-gray-100 dark:hover:bg-white/5 dark:text-white"
                >
                  <RefreshCw className="w-4 h-4" />
                  Retake
                </button>
                <button
                  type="button"
                  onClick={handleUseThis}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white"
                >
                  <Check className="w-4 h-4" />
                  Use this
                </button>
              </div>
              <p className="text-center text-sm text-gray-500 dark:text-white/50">
                Scanned document will be saved as an image and added to your
                documents.
              </p>
            </div>
          )}

          {step === 'metadata' && (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm text-gray-700 dark:text-white/70 mb-1">
                  Document Type <span className="text-red-500">*</span>
                </label>
                <select
                  required
                  value={formData.type}
                  onChange={(e) =>
                    setFormData({ ...formData, type: e.target.value })
                  }
                  className="w-full px-4 py-2 border border-gray-300 dark:border-white/8 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-[#0F0E16] dark:text-white"
                >
                  <option value="">Select type...</option>
                  {documentTypes.map((t) => (
                    <option key={t.value} value={t.value}>
                      {t.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm text-gray-700 dark:text-white/70 mb-1">
                  Document Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  className="w-full px-4 py-2 border border-gray-300 dark:border-white/8 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-[#0F0E16] dark:text-white"
                  placeholder="e.g., Rabies Vaccination Certificate 2024"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-700 dark:text-white/70 mb-1">
                  Document Date <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  required
                  value={formData.date}
                  onChange={(e) =>
                    setFormData({ ...formData, date: e.target.value })
                  }
                  className="w-full px-4 py-2 border border-gray-300 dark:border-white/8 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-[#0F0E16] dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-700 dark:text-white/70 mb-1">
                  Notes (optional)
                </label>
                <textarea
                  value={formData.notes}
                  onChange={(e) =>
                    setFormData({ ...formData, notes: e.target.value })
                  }
                  rows={2}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-white/8 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-[#0F0E16] dark:text-white"
                  placeholder="Additional information..."
                />
              </div>
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setStep('preview')}
                  className="flex-1 py-2.5 rounded-lg border dark:border-white/10 dark:text-white"
                >
                  Back
                </button>
                <button
                  type="submit"
                  disabled={uploading}
                  className="flex-1 py-2.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {uploading ? (
                    <>
                      <span className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full" />
                      Uploading...
                    </>
                  ) : (
                    <>
                      <Check className="w-5 h-5" />
                      Save document
                    </>
                  )}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
